import React, { useState } from "react";
import Resources from "../../types/Resources";
import Department from "../../types/Department";
import Role from "../../types/Role";
import { CodeRole } from "../../common/enums";
import UserRoleTableRow from "./UserRoleTableRow";
import { DepartmentRoleOptions } from "../../types/DepartmentRoleOptions";
import Button from "../Common/Button";

type UserRolesTableProps = {
  roles: Role[];
  resources: Resources;
  departmentRoleOptions: DepartmentRoleOptions[];
  defaultShowNewUserRoleRow?: boolean;
  showNewUserRoleRow: boolean;
  errorMessage?: string;
  onRoleAdded: (role: Role) => void;
  onRoleRemoved: (role: Role) => void;
  onShowNewUserRoleRow: () => void;
  onHideNewUserRoleRow: () => void;
};

function UserRolesTable({
  roles,
  resources,
  departmentRoleOptions,
  onRoleAdded,
  onRoleRemoved,
  showNewUserRoleRow,
  errorMessage,
  onShowNewUserRoleRow,
  onHideNewUserRoleRow,
}: UserRolesTableProps) {
  const [selectedDepartmentId, setSelectedDepartmentId] = useState<number>(0);
  const [selectedRoleCode, setSelectedRoleCode] = useState<CodeRole | "">("");

  function selectedDepartmentChangeHandler(
    event: React.ChangeEvent<HTMLSelectElement>,
  ) {
    const departmentId = Number.parseInt(event.target.value);

    if (isNaN(departmentId)) {
      return;
    }

    setSelectedRoleCode("");
    setSelectedDepartmentId(departmentId);
  }

  function selectedRoleCodeChangeHandler(
    event: React.ChangeEvent<HTMLSelectElement>,
  ) {
    setSelectedRoleCode(event.target.value as CodeRole);
  }

  const selectedDepartmentRoleOptions = selectedDepartmentId
    ? departmentRoleOptions.find((d) => d.departmentId === selectedDepartmentId)
    : null;

  function saveRoleClickHandler() {
    if (!selectedDepartmentId || !selectedRoleCode) {
      return;
    }

    const isDuplicate = roles.some(
      (r) =>
        r.departmentId === selectedDepartmentId &&
        r.roleCode === selectedRoleCode,
    );
    if (isDuplicate) {
      setSelectedDepartmentId(0);
      setSelectedRoleCode("");
      onHideNewUserRoleRow();
      return;
    }

    const newRole: Role = {
      departmentId: selectedDepartmentId,
      departmentName: selectedDepartmentRoleOptions?.departmentName ?? "",
      roleCode: selectedRoleCode,
      name: `${selectedRoleCode}:${selectedDepartmentId}`,
      canRemoveUserFromRole: true,
    };

    onRoleAdded(newRole);

    setSelectedDepartmentId(0);
    setSelectedRoleCode("");
    onHideNewUserRoleRow();
  }

  const rows = roles.map((r, i) => (
    <UserRoleTableRow
      onRemoveUserRole={onRoleRemoved}
      role={r}
      resources={resources}
      roleIndex={i}
      key={r.name}
    />
  ));

  const departmentOptions = departmentRoleOptions.map((d) => (
    <option key={d.departmentId} value={d.departmentId}>
      {d.departmentName}
    </option>
  ));

  const roleCodeOptions =
    selectedDepartmentRoleOptions &&
    selectedDepartmentRoleOptions.codeRoles.length > 0
      ? selectedDepartmentRoleOptions.codeRoles.map((codeRole) => (
          <option key={codeRole} value={codeRole}>
            {resources.get(codeRole)}
          </option>
        ))
      : [];

  const disableSaveNewRole = !selectedDepartmentId || !selectedRoleCode;

  return (
    <div className="col-12 mt-3">
      <table className="table table-bordered ">
        <thead>
          <tr style={{ backgroundColor: "#6B757D" }}>
            <th className="fw-bold text-white">
              {resources.get("Department")}
            </th>
            <th className="fw-bold text-white">{resources.get("UserRole")}</th>
            <th
              className="fw-bold text-white text-center"
              style={{ width: "1px" }}
            >
              {resources.get("Alternatives")}
            </th>
          </tr>
        </thead>

        <tbody>{rows}</tbody>
        {showNewUserRoleRow ? (
          <tfoot>
            <tr>
              <td className="col-5">
                <select
                  className="form-select"
                  value={selectedDepartmentId}
                  onChange={selectedDepartmentChangeHandler}
                >
                  <option value="0" disabled>
                    -- {resources.get("Department")} --
                  </option>
                  {departmentOptions}
                </select>
              </td>
              <td className="col-5">
                <select
                  className="form-select"
                  value={selectedRoleCode}
                  onChange={selectedRoleCodeChangeHandler}
                >
                  <option value="" disabled>
                    -- {resources.get("UserRole")} --
                  </option>
                  {roleCodeOptions}
                </select>
              </td>
              <td className="col-2">
                <div className="d-flex gap-2 justify-content-center">
                  <Button
                    type="button"
                    variant="success"
                    onClick={saveRoleClickHandler}
                    disabled={disableSaveNewRole}
                  >
                    <i className="fas fa-check rounded"></i>
                  </Button>
                  <Button
                    type="button"
                    variant="danger"
                    onClick={onHideNewUserRoleRow}
                  >
                    <i className="fas fa-undo rounded"></i>
                  </Button>
                </div>
              </td>
            </tr>
          </tfoot>
        ) : null}
      </table>
      <span className="text-danger">{errorMessage}</span>
      <div className="d-inline-flex w-100 justify-content-end flex-column align-items-end">
        <button
          type="button"
          className="btn btn-success float-end"
          onClick={onShowNewUserRoleRow}
          disabled={showNewUserRoleRow}
        >
          {resources.get("AddNewUserRole")}
        </button>
      </div>
    </div>
  );
}

export default UserRolesTable;
